import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { ServiceStatusResponse } from '../../types';
import { apiRequest } from '../../services/api/axiosConfig';
import TrialExpiredModal from './TrialExpiredModal';
import tokenManager from '../../utils/tokenManager';

const TrialExpiredProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.organization.organization);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatusResponse | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [trialExpiredFromApi, setTrialExpiredFromApi] = useState(false);

  // Listen for trial_expired events from Axios interceptor
  useEffect(() => {
    const handler = () => {
      setTrialExpiredFromApi(true);
    };
    window.addEventListener('trial_expired', handler);
    return () => window.removeEventListener('trial_expired', handler);
  }, []);

  // Fetch service status when user is authenticated
  const fetchServiceStatus = useCallback(async () => {
    try {
      const status = await apiRequest.get<ServiceStatusResponse>('/service-status');
      setServiceStatus(status);
    } catch {
      // Ignore errors - service status is best-effort
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchServiceStatus();
    }
  }, [user, fetchServiceStatus]);

  // Determine whether to show the modal
  useEffect(() => {
    // From API interceptor (403 trial_expired)
    if (trialExpiredFromApi) {
      setShowModal(true);
      return;
    }

    // From organization data
    if (organization && organization.can_use_service === false && organization.has_trial_expired === true) {
      setShowModal(true);
      return;
    }

    // From service status API
    if (serviceStatus && !serviceStatus.can_use_service && serviceStatus.has_trial_expired) {
      setShowModal(true);
      return;
    }

    setShowModal(false);
  }, [organization, serviceStatus, trialExpiredFromApi]);

  const handleLogout = useCallback(async () => {
    try {
      const authService = (await import('../../services/api/authService')).default;
      await authService.logout();
    } catch {
      // Force logout even if API call fails
    }
    tokenManager.clearAllAuthData();
    window.location.href = '/login';
  }, []);

  // Get user role and leaders from best available source
  const userRole = serviceStatus?.user_role || user?.role || 'member';
  const leaders = serviceStatus?.leaders || [];

  return (
    <>
      {children}
      {showModal && (
        <TrialExpiredModal
          userRole={userRole}
          leaders={leaders}
          onLogout={handleLogout}
        />
      )}
    </>
  );
};

export default TrialExpiredProvider;
